import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import BuildingCustomButton from "./BuildingCustomButton";
import {translate} from "../../types/EnumTranslations";
import COLORS, {
    analyzeComponents,
    countBuildingImages,
    getCoverPhotoStoragePath,
    getHazardWarnings,
    getRiskWarnings,
    getRiskWarningsFromBuilding,
    getAllUniqueHazardsFromBuilding
} from "../../Helpers/utils";
import ClickableImage from "../../Components/ClickableImage";
import {MapContainer, Marker, TileLayer} from "react-leaflet";
import ButtonWithIcon from "./ButtonWithIcon";
import button_contacts from "../../Images/button-icons/button_contacts.png"
import button_maintenance from '../../Images/Meny-ikoner/ikon_forvaltning.png';
import button_files from "../../Images/button-icons/button_files.png"
import button_images from "../../Images/button-icons/button_images.png"
import button_checks from "../../Images/button-icons/ruler.png"
import up_right_arrow from "../../Images/button-icons/up_right_arrow.png"
import {Button, Spinner} from "react-bootstrap";
import compass from "../../Images/compass.png"
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import noReception from "../../Images/no_reception.png"
import "./BuildingPage.css"
import {BaseType} from "../../types/Enums";
import {BaseComponent, getToiletComponentTranslation} from "../../types/protocols";
import {BaseComponentImpl, LevelImpl} from "../../types/implementations";
import { Helmet } from 'react-helmet-async';
import {useBuilding} from "../../Helpers/BuildingContext";
import {placeholderForBuildingType} from "../../Helpers/PlaceholderUtils";
import {FirebaseImage, getImagePathWithSize, ImageSize, useFirebaseImage} from "../../Helpers/ImageLoader";
import {Fancybox} from "@fancyapps/ui";
import {BuildingType} from "../../types/BuildingTypeEnums";
import NestedRiskConstruction from "../../Components/NestedRiskConstruction";
import NestedHazardView from "../../Components/NestedHazardView";

// Info tooltip component
const InfoTooltip: React.FC<{
    title: string;
    tooltip: string;
    isVisible: boolean;
    onClick: () => void;
}> = ({ title, tooltip, isVisible, onClick }) => {
    return (
        <div className="info-header-container">
            <b className="info-title">{title}</b>
            <div className="info-tooltip-wrapper">
                <button className="info-icon" onClick={onClick}>
                    i
                </button>
                {isVisible && (
                    <div className="info-tooltip">
                        {tooltip}
                        <div className="info-tooltip-arrow" />
                    </div>
                )}
            </div>
        </div>
    );
};

const BuildingPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isPoorReceptionExpanded, setIsPoorReceptionExpanded] = useState(false);
    const [showSeasonInfoTooltip, setShowSeasonInfoTooltip] = useState(false);
    const [showHeritageTooltip, setShowHeritageTooltip] = useState(false);
    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();

    const coverImage = currentBuilding ? getImagePathWithSize(getCoverPhotoStoragePath(currentBuilding.summary), ImageSize.FULL) : null;
    const { src, isLoading, hasError } = useFirebaseImage(coverImage, 'placeholder');

    // Extract buildingKey from URL
    const pathSegments = location.pathname.split('/');
    const buildingKey = pathSegments[pathSegments.length - 1];

    useEffect(() => {
        const loadBuildingData = async () => {
            await fetchBuilding(buildingKey);
        };

        loadBuildingData().then();
    }, [location.pathname, fetchBuilding, buildingKey]);

    // Handle clicks outside tooltips
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.info-tooltip-wrapper')) {
                setShowSeasonInfoTooltip(false);
                setShowHeritageTooltip(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    if (buildingError) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '50vh',
                padding: '20px'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '40px',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    maxWidth: '400px',
                    width: '100%'
                }}>
                    <h3 style={{
                        color: '#d32f2f',
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '16px'
                    }}>Fel uppstod</h3>
                    <p style={{
                        color: '#666',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        margin: 0
                    }}>{buildingError}</p>
                </div>
            </div>
        );
    }

    if (loadingBuilding) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                gap: '20px'
            }}>
                <Spinner
                    animation="border"
                    role="status"
                    style={{
                        width: '3rem',
                        height: '3rem',
                        color: COLORS.primaryDark
                    }}
                >
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (!currentBuilding) {
        return <>Byggnad saknas</>
    }

    const { model, summary } = currentBuilding;

    const riskConstructions = getRiskWarningsFromBuilding(currentBuilding)
    const hazards = getAllUniqueHazardsFromBuilding(currentBuilding)

    const filterComponentsByType = (components: { [key: string]: BaseComponent } | undefined, type: BaseType) =>
        Object.values(components || {}).filter(component => component.type === type);

    const roofComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Roof) : [];
    const facadeComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Facade) : [];
    const foundationComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Foundation) : [];
    const drainageComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.RoofDrainage) : [];
    const groundDrainageComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.GroundDrainage) : [];
    const chimneyComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Chimney) : [];
    const outdoorComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Outdoor) : [];
    const techComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Utility) : [];

    return (
        <div style={{marginBottom: '20px'}}>
            <Helmet>
                <title>{summary.name ? `${summary.name} - Molnfastigheter` : 'Byggnad - Molnfastigheter'}</title>
            </Helmet>
            <BreadcrumbComponent
                items={[
                    {text: summary.name ?? 'Byggnad'},
                ]}
            />

            <div style={{ padding: '20px', backgroundColor: '#f4f4f4', width: '100%', height: '100%', maxWidth: '1100px' }}>
                <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '240px',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    overflow: 'hidden'
                }} >
                    <FirebaseImage
                        storagePath={getCoverPhotoStoragePath(summary)}
                        size={ImageSize.FULL}
                        placeholder={placeholderForBuildingType(currentBuilding.summary.buildingType)}
                        alt="Background"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            cursor: coverImage ? 'pointer' : 'default'
                        }}
                        onClick={()=> {
                            if (coverImage) {
                                Fancybox.show(
                                    [
                                        {
                                            src: src,
                                            caption: "Omslagsbild",
                                        }
                                    ],
                                    {
                                        animated: true,
                                        showClass: "fancybox-fadeIn",
                                    }
                                );
                            }
                        }}
                    />

                    {summary.coordinates?.latitude && summary.coordinates?.longitude && (
                        <div style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            width: '210px',
                            height: '210px',
                            borderRadius: '10px',
                            zIndex: 2,
                            overflow: 'hidden',
                            boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)',
                        }} >
                            <MapContainer
                                center={[summary.coordinates.latitude, summary.coordinates.longitude]}
                                zoom={13}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                />
                                <Marker position={[summary.coordinates.latitude, summary.coordinates.longitude]} />
                            </MapContainer>

                        </div>
                    )}
                </div>

                <div
                    style={{
                        backgroundColor: 'white',
                        borderBottomLeftRadius: '10px',
                        borderBottomRightRadius: '10px',
                        padding: '20px',
                        paddingBottom: '30px',
                        marginBottom: '40px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        fontSize: '10pt',
                    }}
                >

                    <div style={{display: 'flex', gap: '20px', alignItems: 'center'}}>
                        <span style={{fontSize: '30pt'}}>{summary.name}</span>
                        <div style={{
                            fontSize: '14pt',
                            backgroundColor: COLORS.lightGrey,
                            borderRadius: '6px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '12px',
                            paddingRight: '12px'
                        }}>
                            {translate(summary.buildingType)}
                            {
                                (summary.buildingType === BuildingType.toiletReserve || summary.buildingType === BuildingType.toiletArchipelago) &&
                                ` – ${getToiletComponentTranslation(summary, model)}`
                            }
                        </div>



                    </div>
                    <br/>

                    <div style={{ display: 'flex', gap: '40px' }}>

                        {/* First Column: Buttons */}
                        <div style={{ flex: 1 }}>
                            <div>
                                <ButtonWithIcon path={`/building/${buildingKey}/documents`} imagePath={button_files}>
                                    Dokument & anteckningar
                                    ({model.documents ?
                                    <>{Object.values(model.documents).length}</> :
                                    <>0</>
                                })
                                </ButtonWithIcon>

                                <ButtonWithIcon path={`/building/${buildingKey}/allPhotos`} imagePath={button_images}>
                                    Samtliga bilder
                                    ({countBuildingImages(currentBuilding)})
                                </ButtonWithIcon>

                                <ButtonWithIcon path={`/building/${buildingKey}/editContacts`} imagePath={button_contacts}>
                                    Kontakter
                                    ({model.contactKeys ?
                                    <>{model.contactKeys.length ?? 0}</>
                                    :
                                    <>0</>})
                                </ButtonWithIcon>

                                <ButtonWithIcon path={`/maintenanceActive/${buildingKey}`} imagePath={button_maintenance}>
                                    Underhållsplan
                                </ButtonWithIcon>

                                <ButtonWithIcon path={`/building/${buildingKey}/checks`} imagePath={button_checks}>
                                    Kontroller
                                </ButtonWithIcon>

                            </div>
                        </div>

                        {/* Second Column: Address */}
                        <div style={{ flex: 1 }}>
                            <b>Adress</b>
                            <br/>
                            {
                                (summary.buildingInfo?.street || summary.buildingInfo?.postalCode) ?
                                    <>
                                        {summary.buildingInfo?.street ?? "[Gatuadress]"}
                                        <br/>
                                        {`${summary.buildingInfo?.postalCode ?? "[Postnummer]"} ${summary.buildingInfo?.city ?? "[Postort]"}`}
                                    </>
                                    :
                                    <>-</>
                            }
                            <br/>
                            <br/>

                            <b>Kommun</b>
                            <br/>
                            {summary.buildingInfo?.municipality ?
                                summary.buildingInfo?.municipality
                                :
                                "-"
                            }
                            <br/>
                            <br/>

                            {
                                summary.buildingInfo?.yearBuilt &&
                                    <>
                                        <b>Byggnadsår</b>
                                        <br/>
                                        {summary.buildingInfo.yearBuilt}
                                        <br/>
                                        <br/>
                                    </>
                            }

                            {
                                summary?.coordinates?.latitude && summary?.coordinates?.longitude && (
                                    <>
                                        <b>Koordinater</b>
                                        <br/>
                                        <span style={{fontSize: '9pt'}}> {`${summary.coordinates.latitude}, ${summary.coordinates.longitude}`}</span>
                                        <br/>

                                        <Button
                                            variant="secondary"
                                            style={{
                                                fontSize: '9pt',
                                                borderRadius: '12px',
                                                padding: '2px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginTop: '4px',
                                            }}
                                            onClick={() => window.open(
                                                `https://maps.google.com/?q=${summary.coordinates?.latitude},${summary.coordinates?.longitude}`,
                                                '_blank',
                                                'noopener,noreferrer')}
                                        >
                                            Google Maps
                                            <img src={up_right_arrow}
                                                 alt={"Up Right Arrow"}
                                                 style={{
                                                     width: '14px',
                                                     height: '14px',
                                                     marginLeft: '3px',
                                                     filter: 'brightness(0) invert(1)',
                                                 }}

                                            />
                                        </Button>
                                    </>
                                )
                            }
                        </div>

                        {/* Third Column: Contact Info */}
                        <div style={{ flex: 1 }}>
                            <p>
                                <b>Nyckelinstruktioner</b>
                                <br/>
                                {summary.buildingInfo?.keyInstructions ?? "-"}
                            </p>
                            <p>
                                <b>Övrigt</b>
                                <br/>
                                {summary.buildingInfo?.other ?? "-"}
                            </p>
                            {
                                summary.buildingInfo?.seasonInfo?.comment &&
                                <p>
                                    <InfoTooltip
                                        title="Öppningsperiod"
                                        tooltip="Information om när byggnaden är tillgänglig eller öppen för besök"
                                        isVisible={showSeasonInfoTooltip}
                                        onClick={() => setShowSeasonInfoTooltip(!showSeasonInfoTooltip)}
                                    />
                                    {summary.buildingInfo?.seasonInfo?.comment}
                                </p>
                            }

                            { summary.buildingInfo?.heritageProtectionLevel && (() => {
                                const levels = summary.buildingInfo.heritageProtectionLevel;
                                return (
                                    <p>
                                        <InfoTooltip
                                            title="Kulturvärdesnivå"
                                            tooltip="Beskriver byggnaders kulturhistoriska skyddsnivåer i syfte att stödja planering, tillståndsprövning och underhåll med hänsyn till bevarandevärden."
                                            isVisible={showHeritageTooltip}
                                            onClick={() => setShowHeritageTooltip(!showHeritageTooltip)}
                                        />
                                        {levels.map((level, index) => (
                                            <React.Fragment key={index}>
                                                {translate(level)}
                                                {index < levels.length - 1 && <br/>}
                                            </React.Fragment>
                                        ))}
                                    </p>
                                );
                            })()
                            }

                            {
                                summary.buildingInfo?.hasPoorReception &&
                                <div>
                                    <div
                                        onClick={() => setIsPoorReceptionExpanded(!isPoorReceptionExpanded)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',}}>
                                        <img
                                            style={{width: '22px', height: '22px', marginRight: '10px'}}
                                            src={noReception}
                                            alt="No reception icon"
                                        />
                                        <span style={{color: '#DC143C', fontWeight: 'bold', fontSize: '9pt'}}>
                                            Ingen mottagning
                                        </span>
                                        <button
                                            style={{
                                                marginLeft: '5px',
                                                background: 'none',
                                                border: 'none',
                                                padding: '5px',
                                                color: '#DC143C'
                                            }}
                                        >
                                            {isPoorReceptionExpanded ? '▼' : '▲'}
                                        </button>
                                    </div>
                                    {isPoorReceptionExpanded && (
                                        <p style={{ marginTop: '10px' }}>
                                            Det finns ingen (eller väldigt bristfällig) mobiltäckning vid byggnaden.
                                            Viktigt att använda off grid-mode vid användning av Molnfastigheters App vid denna byggnad.
                                        </p>
                                    )}
                                </div>
                            }
                        </div>

                        <div style={{ flex: 1}}>
                            {
                                model.currentMapImageKey ?
                                    model.currentMapImageKey && model.mapImages &&
                                    <div style={{
                                        width: '175px',
                                        height: '175px',
                                        position: 'relative'
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%'
                                        }}>
                                            <ClickableImage
                                                buildingKey={model.key}
                                                photo={model.mapImages[model.currentMapImageKey]}
                                                size={175}
                                                showDate={false}
                                            />
                                        </div>
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            zIndex: 1,
                                            pointerEvents: 'none'
                                        }}>
                                            <img
                                                src={compass}
                                                alt={"Compass"}
                                                style={{
                                                    padding: '8px',
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    :
                                    model.mapImage &&
                                    <div style={{
                                        width: '175px',
                                        height: '175px',
                                        position: 'relative'
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%'
                                        }}>
                                            <ClickableImage
                                                buildingKey={model.key}
                                                photo={model.mapImage}
                                                size={175}
                                                showDate={false}
                                            />
                                        </div>
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            zIndex: 1,
                                            pointerEvents: 'none'
                                        }}>
                                            <img
                                                src={compass}
                                                alt={"Compass"}
                                                style={{
                                                    padding: '8px',
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                            />
                                        </div>
                                    </div>
                            }

                            {

                            }
                        </div>
                    </div>

                    <div style={{ display: 'flex', columnGap: '40px', rowGap: '25px', flexWrap: 'wrap', marginTop: '30px' }}>

                        {/* MILJÖ / HÄLSOFARA */}
                        {
                            hazards.length !== 0 &&
                            <NestedHazardView 
                                hazards={hazards}
                                buildingKey={currentBuilding.key}
                            />
                        }

                        {
                            riskConstructions.length !== 0 &&
                            <NestedRiskConstruction 
                                riskConstructions={riskConstructions}
                                buildingKey={currentBuilding.key}
                            />
                        }

                    </div>

                </div>

                <div
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        padding: '20px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>

                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <h3 style={{ paddingBottom: '10px' }}>{"Klimatskal"}</h3>
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {roofComponents && (() => {
                                    const metadata = analyzeComponents(Object.values(roofComponents), currentBuilding || undefined);
                                    
                                    // Count child components attached to roof components
                                    let roofSheetMetalCount = 0;
                                    let roofChimneyCount = 0;
                                    let roofDrainageCount = 0;
                                    
                                    if (currentBuilding?.model?.structure?.components) {
                                        roofComponents.forEach(roofComp => {
                                            Object.values(currentBuilding.model.structure?.components || {}).forEach(comp => {
                                                const parentSurface = (comp as any).parentSurface?.value;
                                                if (parentSurface === roofComp.key) {
                                                    if (comp.type === BaseType.SheetMetal) {
                                                        roofSheetMetalCount++;
                                                    } else if (comp.type === BaseType.Chimney) {
                                                        roofChimneyCount++;
                                                    } else if (comp.type === BaseType.RoofDrainage) {
                                                        roofDrainageCount++;
                                                    }
                                                }
                                            });
                                        });
                                    }
                                    
                                    if (roofSheetMetalCount > 0) {
                                        metadata.push(`${roofSheetMetalCount} plåtarbeten`);
                                    }
                                    if (roofChimneyCount > 0) {
                                        metadata.push(`${roofChimneyCount} ${translate(BaseType.Chimney).toLowerCase()}`);
                                    }
                                    if (roofDrainageCount > 0) {
                                        metadata.push(`${roofDrainageCount} ${translate(BaseType.RoofDrainage).toLowerCase()}`);
                                    }
                                    
                                    return (
                                        <BuildingCustomButton
                                            title={translate(BaseType.Roof)}
                                            metadata={metadata}
                                            imagePath={'Basetypes/roof.png'}
                                            onClick={()=> {
                                                navigate(`${location.pathname}/roof`)
                                            }}/>
                                    );
                                })()}

                                {drainageComponents && <BuildingCustomButton
                                    title={translate(BaseType.RoofDrainage)}
                                    metadata={analyzeComponents(drainageComponents, currentBuilding || undefined)}
                                    imagePath={'Basetypes/roof_drainage.png'}
                                    onClick={()=> {
                                        navigate(`${location.pathname}/roof_drainage`)
                                    }}/>
                                }

                                {groundDrainageComponents && <BuildingCustomButton
                                    title={translate(BaseType.GroundDrainage)}
                                    metadata={analyzeComponents(groundDrainageComponents, currentBuilding || undefined)}
                                    imagePath={'Basetypes/ground_drainage.png'}
                                    onClick={()=> {
                                        navigate(`${location.pathname}/ground_drainage`)
                                    }}/>
                                }

                                {chimneyComponents && <BuildingCustomButton
                                    title={translate(BaseType.Chimney)}
                                    metadata={analyzeComponents(chimneyComponents, currentBuilding || undefined)}
                                    imagePath={'Basetypes/chimney.png'}
                                    onClick={()=> {
                                        navigate(`${location.pathname}/chimney`)
                                    }}/>
                                }

                                {facadeComponents && (() => {
                                    const metadata = analyzeComponents(facadeComponents, currentBuilding || undefined);
                                    
                                    // Count child components attached to facade components
                                    let facadeSheetMetalCount = 0;
                                    let facadeChimneyCount = 0;
                                    let facadeDrainageCount = 0;
                                    
                                    if (currentBuilding?.model?.structure?.components) {
                                        facadeComponents.forEach(facadeComp => {
                                            Object.values(currentBuilding.model.structure?.components || {}).forEach(comp => {
                                                const parentSurface = (comp as any).parentSurface?.value;
                                                if (parentSurface === facadeComp.key) {
                                                    if (comp.type === BaseType.SheetMetal) {
                                                        facadeSheetMetalCount++;
                                                    } else if (comp.type === BaseType.Chimney) {
                                                        facadeChimneyCount++;
                                                    } else if (comp.type === BaseType.RoofDrainage) {
                                                        facadeDrainageCount++;
                                                    }
                                                }
                                            });
                                        });
                                    }
                                    
                                    if (facadeSheetMetalCount > 0) {
                                        metadata.push(`${facadeSheetMetalCount} plåtarbeten`);
                                    }
                                    if (facadeChimneyCount > 0) {
                                        metadata.push(`${facadeChimneyCount} ${translate(BaseType.Chimney).toLowerCase()}`);
                                    }
                                    if (facadeDrainageCount > 0) {
                                        metadata.push(`${facadeDrainageCount} ${translate(BaseType.RoofDrainage).toLowerCase()}`);
                                    }
                                    
                                    return (
                                        <BuildingCustomButton
                                            title={translate(BaseType.Facade)}
                                            metadata={metadata}
                                            imagePath={'Basetypes/facade.png'}
                                            onClick={()=> {
                                                navigate(`${location.pathname}/facade`)
                                            }}/>
                                    );
                                })()}

                                {foundationComponents && <BuildingCustomButton
                                    title={translate(BaseType.Foundation)}
                                    metadata={analyzeComponents(foundationComponents, currentBuilding || undefined)}
                                    imagePath={'Basetypes/foundation.png'}
                                    onClick={()=> {
                                        navigate(`${location.pathname}/foundation`)
                                    }}/>
                                }

                                {(() => {
                                    // Count total windows in the building
                                    let windowCount = 0;
                                    if (currentBuilding?.model?.structure?.components) {
                                        Object.values(currentBuilding.model.structure.components).forEach(comp => {
                                            if (comp.type === BaseType.Window) {
                                                windowCount++;
                                            }
                                        });
                                    }
                                    
                                    if (windowCount > 0) {
                                        return (
                                            <BuildingCustomButton
                                                title="Fönster"
                                                metadata={[`${windowCount} st`]}
                                                imagePath={'types/window.png'}
                                                onClick={() => {
                                                    navigate(`${location.pathname}/windows`)
                                                }}
                                            />
                                        );
                                    }
                                    return null;
                                })()}

                                {(() => {
                                    // Count total sheet metal components in the building
                                    let sheetMetalCount = 0;
                                    if (currentBuilding?.model?.structure?.components) {
                                        Object.values(currentBuilding.model.structure.components).forEach(comp => {
                                            if (comp.type === BaseType.SheetMetal) {
                                                sheetMetalCount++;
                                            }
                                        });
                                    }
                                    
                                    if (sheetMetalCount > 0) {
                                        return (
                                            <BuildingCustomButton
                                                title={translate(BaseType.SheetMetal)}
                                                metadata={[`${sheetMetalCount} st`]}
                                                imagePath={'materials/sheet_metal.png'}
                                                onClick={() => {
                                                    navigate(`${location.pathname}/sheet-metal`)
                                                }}
                                            />
                                        );
                                    }
                                    return null;
                                })()}
                            </ul>
                        </div>

                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <h3 style={{ paddingBottom: '10px' }}>Våningsplan</h3>
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {model?.structure?.levels &&
                                    Object.entries(model.structure.levels)
                                        .sort(([, levelA], [, levelB]) => levelB.number - levelA.number)
                                        .map(([key, level]) => {
                                            const roomsForLevel = model.structure?.rooms
                                                ? Object.values(model.structure.rooms).filter(room => room.levelKey === level.key)
                                                : [];

                                            /*const totalArea = roomsForLevel.reduce((sum, room) => {
                                                return sum + (typeof room.area === 'number' ? room.area : 0);
                                            }, 0);*/

                                            const metadata = [
                                                `Antal rum: ${roomsForLevel.length}`,
                                                //...(totalArea > 0 ? [`Total area: ${totalArea} kvm`] : [])
                                            ];

                                            let imageString = "mellanvaning.png";
                                            if (level.number < 1) {
                                                imageString = "nedervaning.png";
                                            } else if (level.number > 1) {
                                                imageString = "overvaning.png";
                                            }

                                            const levelImpl = new LevelImpl(level);
                                            return (
                                                <BuildingCustomButton
                                                    key={key}
                                                    title={levelImpl.displayName() || translate(level.type) || "Odefinierat rum"}
                                                    metadata={metadata}
                                                    imagePath={imageString}
                                                    onClick={() => navigate(`${location.pathname}/level/${key}`)}
                                                />
                                            );
                                        })
                                }
                            </ul>
                        </div>

                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <h3 style={{ paddingBottom: '10px' }}>{"Utemiljö"}</h3>
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {outdoorComponents &&
                                    Object.entries(outdoorComponents).map(([key, item]) => {
                                        const outdoor = new BaseComponentImpl(item)
                                        return (
                                            <BuildingCustomButton
                                                title={outdoor.displayName(currentBuilding) || translate(item.specificType) || 'Odefinierat'}
                                                metadata={[translate(item.specificType)]}
                                                onClick={()=> {
                                                    navigate(`${location.pathname}/component/${item.key}`)}
                                                }
                                                imagePath={`BuildTypes/${item.specificType}.png`}
                                            />
                                        );
                                    })}
                            </ul>
                        </div>

                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <h3 style={{ paddingBottom: '10px' }}>{"Fastighetsteknik"}</h3>
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {techComponents &&
                                    Object.entries(techComponents).map(([key, item]) => {
                                        const outdoor = new BaseComponentImpl(item)
                                        return (
                                            <BuildingCustomButton
                                                title={outdoor.displayName(currentBuilding) || translate(item.specificType) || 'Odefinierat'}
                                                metadata={[translate(item.specificType)]}
                                                onClick={()=> {
                                                    navigate(`${location.pathname}/component/${item.key}`)}
                                                }
                                                imagePath={item.specificType ? `types/${item.specificType}.png` : "placeholder.png"}
                                            />
                                        );
                                    })}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>


    );
};

export default BuildingPage;